import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaMicrochip, FaWifi, FaThermometerHalf, FaArrowRight, FaPhone, FaChartLine, FaCogs, FaLock } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const IoTPage = () => {
    const services = [
        {
            icon: <FaMicrochip size={24} />,
            title: 'Smart Sensors',
            description: 'Deploy intelligent sensors for environmental monitoring, asset tracking, and operational insights.',
            gradient: 'from-amber-500 to-orange-500'
        },
        {
            icon: <FaWifi size={24} />,
            title: 'Connected Infrastructure',
            description: 'Build IoT networks with LoRaWAN, cellular, and WiFi connectivity for seamless data transmission.',
            gradient: 'from-orange-500 to-red-500'
        },
        {
            icon: <FaChartLine size={24} />,
            title: 'Data Analytics',
            description: 'Transform raw IoT data into actionable insights with real-time dashboards and reporting.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaThermometerHalf size={24} />,
            title: 'Environmental Monitoring',
            description: 'Monitor temperature, humidity, air quality, and other environmental factors in real-time.',
            gradient: 'from-teal-500 to-green-500'
        },
        {
            icon: <FaLock size={24} />,
            title: 'IoT Security',
            description: 'Secure your connected devices with encryption, authentication, and network segmentation.',
            gradient: 'from-purple-600 to-violet-600'
        },
        {
            icon: <FaCogs size={24} />,
            title: 'System Integration',
            description: 'Integrate IoT solutions with existing business systems for streamlined operations.',
            gradient: 'from-indigo-600 to-blue-600'
        }
    ];

    const useCases = [
        {
            title: 'Smart Buildings',
            description: 'Optimize HVAC, lighting, and energy usage with intelligent building automation.',
            image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop'
        },
        {
            title: 'Asset Tracking',
            description: 'Real-time location tracking for equipment, vehicles, and valuable assets.',
            image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop'
        },
        {
            title: 'Predictive Maintenance',
            description: 'Prevent equipment failures with sensor-based predictive maintenance alerts.',
            image: 'https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=400&h=300&fit=crop'
        }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-amber-900 to-orange-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-amber-400">IoT Connectivity</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400">IoT</span> Connectivity Solutions
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Connect your world with intelligent IoT solutions that drive efficiency, insights, and innovation across your business.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300">
                                    Explore Solutions
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-amber-600/20 to-orange-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop"
                                    alt="IoT Solutions"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete IoT Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From sensors to analytics, we deliver end-to-end IoT solutions for smarter operations
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-amber-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Use Cases Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-amber-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">Use Cases</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">IoT in Action</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            See how businesses are leveraging IoT to transform their operations
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {useCases.map((useCase, index) => (
                            <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group">
                                <div className="h-48 overflow-hidden">
                                    <img
                                        src={useCase.image}
                                        alt={useCase.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                </div>
                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">{useCase.title}</h3>
                                    <p className="text-gray-600">{useCase.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-500 to-orange-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Connect Your Business?</h2>
                    <p className="text-xl text-amber-100 mb-8">
                        Let us design a custom IoT solution that transforms your operations and drives growth.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-amber-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Start Your IoT Journey
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Speak to an Expert
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default IoTPage;
